import { motion } from "framer-motion";
import "./css/HomePage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Facebook, Twitter, Instagram } from 'lucide-react';
import { useAuth } from "react-oidc-context";

const HomePage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();

  // Scroll to top when navigating to home page
  useEffect(() => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  return (
    <div className="homepage">
      {/* Hero Section with Parallax Effect */}

        <section className="hero">
          {auth.isAuthenticated ? (
            <div className="hero-content">
              <h1>Welcome !!</h1>
              <p>Help your child excel in the NNAT, CoGAT, IAAT and many more with free, online or printable practice tests.</p>
              <p>Start today and boost their confidence!</p>
              <motion.button
                className="cta-button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }} onClick={() => navigate("/account")}
              >
                Access your Dashboard
              </motion.button>
            </div>
          ) : (
            <div className="hero-content">
              <h1>Unlock Your Child's Potential</h1>
              <p>Help your child excel in the NNAT, CoGAT, IAAT and many more with free, online or printable practice tests.</p>
              <p>Start today and boost their confidence!</p>
              <motion.button
                className="cta-button"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }} onClick={() => auth.signinRedirect()}
              >
                Get Started
              </motion.button>
            </div>   
          )}
        </section>


      {/* Exam Categories */}
      <section className="exam-categories">
        <h2>Explore Our Test Preparation</h2>
        <div className="category-container">
          <div className="category-card">
            <h3>CoGAT</h3>
            <p>
              The Cognitive Abilities Test (CoGAT) measures students' reasoning abilities in areas like verbal, quantitative, and nonverbal reasoning. Our prep materials help students understand the test format and sharpen their skills.
            </p>
            <a href="/cogat" className="learn-more">Learn More</a>
          </div>
          <div className="category-card">
            <h3>NNAT</h3>
            <p>
              The Naglieri Nonverbal Ability Test (NNAT) evaluates a child's ability to reason with shapes and patterns. Our resources focus on enhancing spatial reasoning and nonverbal problem-solving skills.
            </p>
            <a href="/nnat" className="learn-more">Learn More</a>
          </div>
          <div className="category-card">
            <h3>IAAT</h3>
            <p>
              The Iowa Algebra Aptitude Test (IAAT) is designed to assess a student's readiness for algebra. Our program helps students strengthen their mathematical thinking and algebraic concepts.
            </p>
            <a href="/iaat" className="learn-more">Learn More</a>
          </div>
          <div className="category-card">
            <h3>Many More</h3>
            <p>
              We offer preparation for many other exams, including SAT, ACT, and more. Explore our full range of test prep materials to help your child succeed.
            </p>
            <a href="/account" className="learn-more">Explore More</a>
          </div>
        </div>
      </section>

      <div className="how-it-works">
        <h2>How It Works</h2>
        <div className="step">
          <h3>📝 Step 1: Become a Member</h3>
          <p>It's free to join! You can either create an account or log in using Gmail or Facebook.</p>
        </div>
        <div className="step">
          <h3>🎯 Step 2: Create Your Tests</h3>
          <p>Choose the tests you want to practice, like NNAT, CoGAT, or IAAT. It's free to get started!</p>
        </div>
        <div className="step">
          <h3>📄 Step 3: Create Printable Practice Tests</h3>
          <p>If you'd like to practice offline, you can create printable practice tests.</p>
        </div>
        <div className="step">
          <h3>⭐ Step 4: Excel in the Actual Tests</h3>
          <p>With consistent practice, you're on your way to excelling in the actual tests!</p>
        </div>
      </div>


      {/* Features Section */}
      <section id="features" className="features">
        <h2 className="section-title">Why Choose Us?</h2>
        <div className="features-container">
          <div className="feature-card">
            <h3 className="feature-title">✨ Expert-Curated Questions</h3>
            <p className="feature-text">Access a wide range of practice questions that closely resemble real test formats.</p>
          </div>
          <div className="feature-card">
            <h3 className="feature-title">🎮 Interactive Practice Tests</h3>
            <p className="feature-text">Engage with interactive practice tests to sharpen your skills and boost your confidence for the big test day!</p>
          </div>
          <div className="feature-card">
            <h3 className="feature-title">📚 Worried About Screen Time?</h3>
            <p className="feature-text">Easily generate printable practice tests for a hands-on study experience.</p>
          </div>
        </div>
      </section>




      {/* Testimonials */}
      <section className="testimonials">
        <h2>What Parents Say</h2>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
          <p>"SmartKidsPrep transformed my child's confidence and test scores!" - Sarah M.</p>
          <p>"The interactive practice tests really helped my son prepare for the COGAT. Highly recommend!" - John D.</p>
          <p>"This platform made test prep fun and engaging for my child. Great results!" - David W.</p>
        </motion.div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-logo">
            <h2>SmartKidsPrep</h2>
            <p>Helping students excel with expert test preparation.</p>
          </div>

          <div className="footer-links">
            <ul>
              <li><a href="/about">About Us</a></li>
              <li><a href="/account">Exam Prep</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-social">
            <div className="social-icons">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <Facebook size={24} strokeWidth={1.5} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <Twitter size={24} strokeWidth={1.5} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <Instagram size={24} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-disclaimer">
          <p>
          All test names and trademarks mentioned on this site belong to their respective owners. SmartKidsPrep.com is an independent resource and is not associated with, endorsed by, or authorized by any test publishers or their affiliates. Any references to specific tests are for descriptive purposes only.
          </p>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} SmartKidsPrep. All rights reserved.</p>
        </div>
      </footer>


    </div>
  );
};

export default HomePage;